import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { LinkedPortalUserDto, UserPermissionFlags } from "@/Common/Models/Portal/MedfarPortal";

interface IAccountUserLinkedProps {
    portalUsers: LinkedPortalUserDto[];
    showInPopup?: boolean;
    showHeader: boolean;
}

class AccountUserLinkedState {
    public portalUsers: LinkedPortalUserDto[];
}

class AccountUserLinked extends React.Component<IAccountUserLinkedProps, AccountUserLinkedState> {
    constructor(props: IAccountUserLinkedProps) {
        super(props);
        this.state = {
            portalUsers: props.portalUsers
        };
    }

    public componentDidUpdate(prevProps: IAccountUserLinkedProps): void {
        if (this.props.portalUsers != null && this.props.portalUsers !== prevProps.portalUsers) {
            this.mutateState(s => s.portalUsers = this.props.portalUsers);
        }
    }

    public render(): JSX.Element {
        let result: JSX.Element = null;

        // remove current user from shared accounts if its there
        const linkedAccounts: LinkedPortalUserDto[] = (this.state.portalUsers ?? []).filter(p => !p.IsOwner);

        let cssClass = "psingle-column centered";
        let limitHeightForPopupDisplay: React.CSSProperties = {};
        if (this.props.showInPopup) {
            cssClass = "";
            limitHeightForPopupDisplay.maxHeight = "130px";
            limitHeightForPopupDisplay.overflowY = "auto";
        }

        let breakLongEmailsCss: React.CSSProperties = { wordBreak: "break-all" };
        let hasSharedAccount = (linkedAccounts != null && linkedAccounts.length > 0);

        result = (
            <div>
                <div className="block separated">
                    {this.props.showHeader && (
                        <div className="flex-container heading">
                            <h3 className="thin-font">
                                {this.props.showInPopup
                                    ? cacheStore.GetString("AccessRightsLabel")
                                    : cacheStore.GetString("AccessRightsLabel") + ": " + cacheStore.GetString("GrantsAccessToLabel")
                                }
                            </h3>
                        </div>
                    )}

                    <div className={cssClass} style={limitHeightForPopupDisplay}>
                        {!hasSharedAccount &&
                            <div className="flex-container center padding-bottom-10"> {cacheStore.GetString("NoSharedAccountLabel")} </div>
                        }

                        {hasSharedAccount && !this.props.showInPopup && (
                            <table className="table">
                                <thead>
                                    <tr>
                                        <th className="center" />
                                        <th className="center">{cacheStore.GetString("EMRLabel")}</th>
                                        <th className="center">{cacheStore.GetString("DocumentsLabel")}</th>
                                        <th className="center">{cacheStore.GetString("MessagesLabel")}</th>
                                        <th className="center">{cacheStore.GetString("ProfileInformationLabel")}</th>
                                        <th className="center">{cacheStore.GetString("AppointmentsLabel")}</th>
                                        <th className="center">{cacheStore.GetString("BillingLabel")}</th>
                                        <th className="center" />
                                    </tr>
                                </thead>
                                <tbody>
                                    {linkedAccounts.map((account, i) => (
                                        <tr key={"accountUserLinked_user_" + i}>
                                            <td className="center" style={account.FullName ? {} : breakLongEmailsCss}>
                                                {account.FullName ? account.FullName : account.Email}
                                            </td>
                                            <td className="center">
                                                <CheckboxControl
                                                    disabled={true}
                                                    checked={this.hasFlag(account.PermissionFlags, UserPermissionFlags.Dossier)}
                                                />
                                            </td>
                                            <td className="center">
                                                <CheckboxControl
                                                    disabled={true}
                                                    checked={this.hasFlag(account.PermissionFlags, UserPermissionFlags.Document)}
                                                />
                                            </td>
                                            <td className="center">
                                                <CheckboxControl
                                                    disabled={true}
                                                    checked={this.hasFlag(account.PermissionFlags, UserPermissionFlags.Message)}
                                                />
                                            </td>
                                            <td className="center">
                                                <CheckboxControl
                                                    disabled={true}
                                                    checked={this.hasFlag(account.PermissionFlags, UserPermissionFlags.Profile)}
                                                />
                                            </td>
                                            <td className="center">
                                                <CheckboxControl
                                                    disabled={true}
                                                    checked={this.hasFlag(account.PermissionFlags, UserPermissionFlags.Appointment)}
                                                />
                                            </td>
                                            <td className="center">
                                                <CheckboxControl
                                                    disabled={true}
                                                    checked={this.hasFlag(account.PermissionFlags, UserPermissionFlags.Billing)}
                                                />
                                            </td>
                                        </tr>
                                    ))}

                                </tbody>
                            </table>
                        )}

                        {hasSharedAccount && this.props.showInPopup && (
                            <table className="table" style={{ tableLayout: "fixed" }}>
                                <thead>
                                    <tr>
                                        <th className="center">{cacheStore.GetString("GrantsAccessToLabel")}</th>
                                        <th className="center">{cacheStore.GetString("SharedInformationLabel")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {linkedAccounts.map((account, i) => (
                                        <tr key={"accountUserSharing_user_" + i}>
                                            <td className="center" style={account.FullName ? {} : breakLongEmailsCss}>
                                                <strong>{account.FullName ? account.FullName : account.Email} </strong>
                                            </td>
                                            <td className="center"> {this.getSharedInformationString(account)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>
        );

        return result;
    }

    private mutateState(mutation: (s: AccountUserLinkedState) => void, then: () => void = null): void {
        var s = new AccountUserLinkedState();
        mutation(s);
        this.setState(s, then);
    }

    private hasFlag(value: UserPermissionFlags, flagToCheck: UserPermissionFlags): boolean {
        return !!(value & flagToCheck);
    }

    private getSharedInformationString(linkedAccount: LinkedPortalUserDto): string {
        var results: string[] = [];

        if (this.hasFlag(linkedAccount.PermissionFlags, UserPermissionFlags.Appointment))
            results.push(cacheStore.GetString("AppointmentsLabel"));
        if (this.hasFlag(linkedAccount.PermissionFlags, UserPermissionFlags.Document))
            results.push(cacheStore.GetString("DocumentsLabel"));
        if (this.hasFlag(linkedAccount.PermissionFlags, UserPermissionFlags.Dossier))
            results.push(cacheStore.GetString("EMRLabel"));
        if (this.hasFlag(linkedAccount.PermissionFlags, UserPermissionFlags.Profile))
            results.push(cacheStore.GetString("ProfileInformationLabel"));
        if (this.hasFlag(linkedAccount.PermissionFlags, UserPermissionFlags.Billing))
            results.push(cacheStore.GetString("BillingLabel"));

        return results.join(", ");
    }
}

export { AccountUserLinked };
